import {Component, OnInit} from '@angular/core';
import {UserNotificationService} from '../../../services/notification/user-notification.service';
import {NotificationService} from '../../../services/common/notification.service';
import {
  NOTIFICATION_TYPE_LABELS,
  NotificationChannel,
  NotificationType,
  UserSettings
} from '../../../dtos/notification/user-notification';
import {folderErrorMessage} from '../../../shared/folder/folder-utils';

interface NotificationSetting {
  type: NotificationType;
  label: string;
  inApp: boolean;
  email: boolean;
}

const SETTINGS_KEYS: { [channel in NotificationChannel]: keyof UserSettings } = {
  inApp: 'inAppNotifications',
  email: 'emailNotifications',
};

/**
 * the user settings: per notification type whether it is shown in the app and whether it is sent by email.
 * Every change is saved immediately. The rows are built from the settings the backend returns, so new
 * notification types appear automatically.
 */
@Component({
  selector: 'app-settings-dialog',
  templateUrl: './settings-dialog.component.html',
  styleUrls: ['./settings-dialog.component.scss'],
  standalone: false
})
export class SettingsDialogComponent implements OnInit {

  readonly displayedColumns = ['label', 'inApp', 'email'];
  settings: NotificationSetting[] = [];
  loading = true;

  constructor(private userNotificationService: UserNotificationService,
              private notificationService: NotificationService) {
  }

  ngOnInit(): void {
    this.userNotificationService.getSettings().subscribe({
      next: settings => {
        this.loading = false;
        const types = new Set([
          ...Object.keys(settings.inAppNotifications ?? {}),
          ...Object.keys(settings.emailNotifications ?? {})
        ]) as Set<NotificationType>;
        this.settings = [...types].map(type => ({
          type,
          // unknown types still get toggles, only without a german label
          label: NOTIFICATION_TYPE_LABELS[type] ?? type,
          inApp: settings.inAppNotifications?.[type] ?? true,
          email: settings.emailNotifications?.[type] ?? true
        }));
      },
      error: error => {
        this.loading = false;
        this.notificationService.openSnackBar(folderErrorMessage(error));
      }
    });
  }

  toggle(setting: NotificationSetting, channel: NotificationChannel, enabled: boolean): void {
    const key = SETTINGS_KEYS[channel];
    setting[channel] = enabled;
    this.userNotificationService.updateSettings({[key]: {[setting.type]: enabled}}).subscribe({
      next: settings => setting[channel] = settings[key]?.[setting.type] ?? enabled,
      error: error => {
        setting[channel] = !enabled;
        this.notificationService.openSnackBar(folderErrorMessage(error));
      }
    });
  }
}
